import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBullhorn, faPlusCircle, faList, faSave, faEraser, faTrash,
  faEdit, faUserCircle, faCaretDown, faUser, faSyncAlt, 
  faSignOutAlt, faCrown, faCheckCircle, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import '../scss/RemoteAlarms.scss';
import BaseURL from '../assets/contants/BaseURL';

const RemoteAlarms = () => {
  const navigate = useNavigate();
  
  // State variables
  const [devices, setDevices] = useState([]);
  const [remoteDevices, setRemoteDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [editId, setEditId] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    deviceName: '',
    deviceToken: '',
    protocol: '',
    pubTopic: '',
    subTopic: '',
    hardwareVersion: '',
    softwareVersion: '',
    apiPath: '',
    deviceStatus: '1'
  });

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  const getAuthHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    return headers;
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const loadDevices = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BaseURL}meters/device/`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        showNotification('Session expired. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }
      
      const data = await response.json();
      setDevices(data);
      
      // Filter devices that start with 'remote_' in device_token
      const remotes = data.filter(device => 
        device.device_token && device.device_token.toLowerCase().startsWith('remote_')
      );
      setRemoteDevices(remotes);
    } catch (error) {
      console.error('Error fetching devices:', error);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const handleAlarmToggle = async (deviceToken, isChecked, alarmItem) => {
    try {
      const response = await fetch('/data/iotmachineresponce/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          device_token: deviceToken,
          value: isChecked ? 1 : 0,
        }),
      });

      if (response.ok) {
        showNotification(
          `Alarm ${deviceToken} ${isChecked ? 'activated' : 'deactivated'} successfully`,
          'success'
        );
      } else {
        showNotification(`Failed to update ${deviceToken}`, 'error');
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error(`Error updating ${deviceToken}:`, error);
      showNotification(`Error updating ${deviceToken}`, 'error');
      throw error;
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      device_name: formData.deviceName,
      device_token: formData.deviceToken,
      protocol: formData.protocol,
      pub_topic: formData.pubTopic,
      sub_topic: formData.subTopic,
      hardware_version: formData.hardwareVersion || null,
      software_version: formData.softwareVersion || null,
      api_path: formData.apiPath || null,
      device_status: parseInt(formData.deviceStatus)
    };
    
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${BaseURL}meters/device/${editId}/` : `${BaseURL}meters/device/`;
    
    try {
      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify(submitData)
      });
      
      if (response.status === 401) {
        showNotification('Session expired. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to save device');
      }
      
      showNotification(
        editId ? 'Device updated successfully' : 'Device added successfully',
        'success'
      );
      
      loadDevices();
      clearForm();
    } catch (error) {
      console.error('Error saving device:', error);
      showNotification('Error saving device', 'error');
    }
  };

  const editDevice = (device) => {
    setFormData({
      deviceName: device.device_name || '',
      deviceToken: device.device_token || '',
      protocol: device.protocol || '',
      pubTopic: device.pub_topic || '',
      subTopic: device.sub_topic || '',
      hardwareVersion: device.hardware_version || '',
      softwareVersion: device.software_version || '',
      apiPath: device.api_path || '',
      deviceStatus: device.device_status?.toString() || '1'
    });
    setEditId(device.id);
    
    // Scroll to form
    document.querySelector('.device-form-container')?.scrollIntoView({ behavior: 'smooth' });
  };

  const deleteDevice = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        const response = await fetch(`${BaseURL}meters/device/${deviceId}/`, {
          method: 'DELETE',
          headers: getAuthHeaders()
        });
        
        if (response.status === 401) {
          showNotification('Session expired. Please login again.', 'error');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
        
        if (!response.ok) {
          throw new Error('Failed to delete device');
        }
        
        showNotification('Device deleted successfully', 'success');
        loadDevices();
        
        if (editId === deviceId) {
          clearForm();
        }
      } catch (error) {
        console.error('Error deleting device:', error);
        showNotification('Error deleting device', 'error');
      }
    }
  };

  const clearForm = () => {
    setFormData({
      deviceName: '',
      deviceToken: '',
      protocol: '',
      pubTopic: '',
      subTopic: '',
      hardwareVersion: '',
      softwareVersion: '',
      apiPath: '',
      deviceStatus: '1'
    });
    setEditId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      sessionStorage.removeItem('Token');
      sessionStorage.removeItem('session_id');
      sessionStorage.removeItem('username');
      localStorage.removeItem('Token');
      localStorage.removeItem('session_id');
      localStorage.removeItem('username');
      
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    }
  };

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  // Filter devices for table (only remote_ devices)
  const tableDevices = Array.isArray(devices) ? devices.filter(device => 
    device.device_token && device.device_token.toLowerCase().startsWith('remote_')
  ) : [];

  return (
    <div className="remote-alarms-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
            <div className="account-dropdown">
              <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {orgLogo ? (
                                  <img
                                    src={orgLogo}
                                    alt="Organization Logo"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '2px solid #4361ee'
                                    }}
                                    onError={() => setOrgLogo(null)}
                                  />
                                ) : (
                                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                )}
                <span>{sessionStorage.getItem('username') || 'User'}</span>
                <FontAwesomeIcon icon={faCaretDown} />
              </div>
              {isDropdownOpen && (
                              <div className="account-menu show">
                                <div className="account-menu-item plan-info">
                                  <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                                  <span>Basic Plan</span>
                                </div>
                                <div className="account-menu-divider"></div>
                                <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                  <FontAwesomeIcon icon={faUser} /> Profile
                                </button>
                                <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                  <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                                </button>
                                <div className="account-menu-divider"></div>
                                <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                                  <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                                </button>
                              </div>
                            )}
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="page-titles">
          <h1>Remote Alarm Controls</h1>
          <p>Toggle remote alarm systems on/off</p>
        </div>

        {/* Remote Alarm Controls */}
        <div className="alarm-controls">
          <h2>Remote Alarm Controls</h2>
          <div className="alarm-grid">
            {remoteDevices.length === 0 ? (
              <div className="no-alarms">No remote alarms configured</div>
            ) : (
              remoteDevices.map((device, index) => (
                <div key={device.id} className="alarm-item" id={`alarm-${device.device_token}`}>
                  <div className="alarm-icon-container">
                    <FontAwesomeIcon icon={faBullhorn} className="alarm-icon" />
                  </div>
                  <div className="alarm-label">{device.device_name || `Remote Alarm ${index + 1}`}</div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      className="alarm-toggle" 
                      data-token={device.device_token}
                      onChange={async (e) => {
                        const alarmItem = e.target.closest('.alarm-item');
                        try {
                          await handleAlarmToggle(device.device_token, e.target.checked, alarmItem);
                        } catch (error) {
                          e.target.checked = !e.target.checked;
                        }
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Device Form */}
        <div className="device-form-container">
          <h2 className="form-title">
            <FontAwesomeIcon icon={faPlusCircle} /> {editId ? 'Edit Remote' : 'Add Remote'}
          </h2>
          <form onSubmit={handleFormSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="deviceName">Remote Name</label>
                <input
                  type="text"
                  id="deviceName"
                  name="deviceName"
                  placeholder="eg: Crane Sensor 1"
                  value={formData.deviceName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="deviceToken">Device Token</label>
                <input
                  type="text"
                  id="deviceToken"
                  name="deviceToken"
                  placeholder="eg: remote_3489209"
                  value={formData.deviceToken}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="protocol">Protocol</label>
                <select
                  id="protocol"
                  name="protocol"
                  className="filter-select" 
                  value={formData.protocol}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Protocol</option>
                  <option value="mqtt">MQTT</option>
                  <option value="http">HTTP</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="pubTopic">Pub Topic</label>
                <input
                  type="text"
                  id="pubTopic"
                  name="pubTopic"
                  placeholder="eg: crane/sensor/data"
                  value={formData.pubTopic}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="subTopic">Sub Topic</label>
                <input
                  type="text"
                  id="subTopic"
                  name="subTopic"
                  placeholder="eg: /status"
                  value={formData.subTopic}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="hardwareVersion">Hardware Version</label>
                <input
                  type="text"
                  id="hardwareVersion"
                  name="hardwareVersion"
                  placeholder="eg: 2.3.0"
                  value={formData.hardwareVersion}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="softwareVersion">Software Version</label>
                <input
                  type="text"
                  id="softwareVersion"
                  name="softwareVersion"
                  placeholder="eg: 1.2.0"
                  value={formData.softwareVersion}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="apiPath">API Path</label>
                <input
                  type="text"
                  id="apiPath"
                  name="apiPath"
                  placeholder="API Path"
                  value={formData.apiPath}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="deviceStatus">Device Status</label>
                <select
                  id="deviceStatus"
                  name="deviceStatus"
                  className="filter-select" 
                  value={formData.deviceStatus}
                  onChange={handleInputChange}
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                <FontAwesomeIcon icon={faSave} /> {editId ? 'Update Remote' : 'Add Remote'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={clearForm}>
                <FontAwesomeIcon icon={faEraser} /> Clear
              </button>
              {editId && (
                <button type="button" className="btn btn-danger" onClick={() => deleteDevice(editId)}>
                  <FontAwesomeIcon icon={faTrash} /> Delete
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Device Table */}
        <div className="device-table-container">
          <h2 className="form-title">
            <FontAwesomeIcon icon={faList} /> Remote List
          </h2>
          
          {isLoading ? (
            <div className="loading-spinner-container">
              <div className="loading-spinner"></div>
              <p>Loading devices...</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="device-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Remote Name</th>
                    <th>Device Token</th>
                    <th>Protocol</th>
                    <th>Pub Topic</th>
                    <th>Sub Topic</th>
                    <th>HW Ver.</th>
                    <th>SW Ver.</th>
                    <th>API Path</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tableDevices.length === 0 ? (
                    <tr>
                      <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>
                        No remote devices found
                      </td>
                    </tr>
                  ) : (
                    tableDevices.map((device, index) => (
                      <tr key={device.id}>
                        <td>{index + 1}</td>
                        <td>{device.device_name || '-'}</td>
                        <td>{device.device_token || '-'}</td>
                        <td>{device.protocol ? device.protocol.toUpperCase() : '-'}</td>
                        <td>{device.pub_topic || '-'}</td>
                        <td>{device.sub_topic || '-'}</td>
                        <td>{device.hardware_version || '-'}</td>
                        <td>{device.software_version || '-'}</td>
                        <td>{device.api_path || '-'}</td>
                        <td className={device.device_status == 1 ? 'status-active' : 'status-inactive'}>
                          {device.device_status == 1 ? 'Active' : 'Inactive'}
                        </td>
                        <td className="action-btns">
                          <button className="edit-btn" onClick={() => editDevice(device)}>
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="delete-btn" onClick={() => deleteDevice(device.id)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default RemoteAlarms;